"use client";

import clsx from "clsx";
import type { MotionValue } from "motion";
import { useMotionValue, useMotionValueEvent, useSpring, useTransform } from "motion/react";
import * as m from "motion/react-m";
import {
  useEffect,
  useId,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";

import { clampValue } from "./HeroMusicUtils";

const BAR_WIDTH = 300;
const BAR_HEIGHT = 10;
const SEGMENT_GAP = 7;
const HALF_SEGMENT_GAP = SEGMENT_GAP / 2;
const STROKE_WIDTH = 4;
const STROKE_RADIUS = STROKE_WIDTH / 2;
const BAR_COLOR = "#FDFCFC";
const GAP_SPRING = {
  stiffness: 180,
  damping: 26,
  mass: 0.3,
};

export const HERO_MUSIC_TIMELINE_GROUP_IDS = ["genres", "languages"] as const;

export type HeroMusicTimelineGroupId = (typeof HERO_MUSIC_TIMELINE_GROUP_IDS)[number];

export type HeroMusicTimelineSection = {
  label: string;
  start: number;
  end: number;
};

export type HeroMusicTimeline = {
  duration: number;
  sections: HeroMusicTimelineSection[];
};

type SectionLabel = {
  slotId: string;
  label: string;
  isPlaceholder: boolean;
  sectionIndex: number;
  width: number;
  x: number;
};

type SegmentTarget = {
  id: string;
  x1: number;
  x2: number;
};

type HeroMusicTimelineBarProps = {
  activeProgress?: MotionValue<number>;
  onSeekProgress?: (progress: number, seconds: number) => void;
  progress: MotionValue<number>;
  sectionSlotCount?: number;
  timeline: HeroMusicTimeline;
};

export function HeroMusicTimelineBar({
  activeProgress,
  onSeekProgress,
  progress,
  sectionSlotCount,
  timeline,
}: HeroMusicTimelineBarProps) {
  const activeSectionIndex = useActiveTimelineSectionIndex(timeline, activeProgress ?? progress);
  const [hoveredSectionIndex, setHoveredSectionIndex] = useState<number | null>(null);
  const resolvedSectionSlotCount = Math.max(sectionSlotCount ?? 0, timeline.sections.length);
  const segments = getSegmentTargets(timeline, resolvedSectionSlotCount);
  const labels = getSectionLabels(timeline, resolvedSectionSlotCount);
  const isInteractive = Boolean(onSeekProgress);

  useEffect(() => {
    setHoveredSectionIndex(null);
  }, [resolvedSectionSlotCount, timeline]);

  return (
    <div
      aria-hidden={isInteractive ? undefined : true}
      className={clsx(
        "tw-relative tw-h-[22px] tw-w-full tw-min-w-0",
        isInteractive ? "tw-pointer-events-auto" : "tw-pointer-events-none",
      )}
    >
      {labels.map(label => (
        <m.span
          animate={{
            left: `${label.x}%`,
            opacity: getLabelOpacity(label, activeSectionIndex, hoveredSectionIndex),
            width: `${label.width}%`,
          }}
          className='tw-pointer-events-none tw-absolute -tw-top-1 tw-z-10 tw-block tw-select-none tw-box-border tw-truncate tw-py-1 tw-pr-1 tw-type-2xs tw-text-white'
          initial={false}
          key={label.slotId}
          title={label.label}
          transition={{
            type: "spring",
            ...GAP_SPRING,
          }}
        >
          {label.label}
        </m.span>
      ))}
      <svg
        className='tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-h-2.5 tw-w-full tw-overflow-visible'
        fill='none'
        height={BAR_HEIGHT}
        preserveAspectRatio='none'
        viewBox={`0 0 ${BAR_WIDTH} ${BAR_HEIGHT}`}
        width={BAR_WIDTH}
        xmlns='http://www.w3.org/2000/svg'
      >
        <g opacity='0.4'>
          {segments.map(segment => (
            <BackgroundSegment key={`background-${segment.id}`} segment={segment} />
          ))}
        </g>
        {segments.map(segment => (
          <ProgressSegment key={`progress-${segment.id}`} progress={progress} segment={segment} />
        ))}
      </svg>
      {onSeekProgress &&
        timeline.sections.map((section, sectionIndex) => (
          <SeekSectionButton
            key={`seek-${section.label}-${sectionIndex}`}
            onHoverSectionChange={setHoveredSectionIndex}
            onSeekProgress={onSeekProgress}
            section={section}
            sectionIndex={sectionIndex}
            timelineDuration={timeline.duration}
          />
        ))}
    </div>
  );
}

function BackgroundSegment({ segment }: { segment: SegmentTarget }) {
  const { opacity, x1, x2 } = useAnimatedSegmentEndpoints(segment);

  return (
    <m.line
      style={{ opacity }}
      stroke={BAR_COLOR}
      strokeLinecap='round'
      strokeWidth={STROKE_WIDTH}
      x1={x1}
      x2={x2}
      y1='5'
      y2='5'
    />
  );
}

function ProgressSegment({ progress, segment }: { progress: MotionValue<number>; segment: SegmentTarget }) {
  const { opacity: segmentOpacity, width, x1, x2 } = useAnimatedSegmentEndpoints(segment);
  const rawClipPathId = useId();
  const clipPathId = `hero-music-segment-clip-${rawClipPathId.replaceAll(":", "")}`;
  const clipX = useTransform(x1, latestX1 => latestX1 - STROKE_RADIUS);
  const clipWidth = useTransform([progress, x1, width], ([latestProgress, latestX1, latestWidth]) => {
    const clipStart = Number(latestX1) - STROKE_RADIUS;
    const segmentWidth = Number(latestWidth);
    const maxClipWidth = segmentWidth + STROKE_WIDTH;
    const currentWidth = clampValue(Number(latestProgress), 0, 1) * BAR_WIDTH - clipStart;

    return clampValue(currentWidth, 0, maxClipWidth);
  });

  return (
    <>
      <defs>
        <clipPath clipPathUnits='userSpaceOnUse' id={clipPathId}>
          <m.rect
            height={STROKE_WIDTH}
            rx={STROKE_RADIUS}
            ry={STROKE_RADIUS}
            width={clipWidth}
            x={clipX}
            y={5 - STROKE_RADIUS}
          />
        </clipPath>
      </defs>
      <m.line
        clipPath={`url(#${clipPathId})`}
        style={{ opacity: segmentOpacity }}
        stroke={BAR_COLOR}
        strokeLinecap='round'
        strokeWidth={STROKE_WIDTH}
        x1={x1}
        x2={x2}
        y1='5'
        y2='5'
      />
    </>
  );
}

function SeekSectionButton({
  onHoverSectionChange,
  onSeekProgress,
  section,
  sectionIndex,
  timelineDuration,
}: {
  onHoverSectionChange: (sectionIndex: number | null) => void;
  onSeekProgress: (progress: number, seconds: number) => void;
  section: HeroMusicTimelineSection;
  sectionIndex: number;
  timelineDuration: number;
}) {
  const sectionDuration = section.end - section.start;

  if (sectionDuration <= 0 || timelineDuration <= 0) return null;

  function stopSectionEvent(event: ReactMouseEvent<HTMLButtonElement> | ReactPointerEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
  }

  function seekToSectionStart() {
    onSeekProgress(timestampToProgress(section.start, timelineDuration), section.start);
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLButtonElement>) {
    stopSectionEvent(event);
    seekToSectionStart();
  }

  function handleClick(event: ReactMouseEvent<HTMLButtonElement>) {
    stopSectionEvent(event);
    if (event.detail === 0) seekToSectionStart();
  }

  return (
    <button
      aria-label={section.label}
      className='tw-absolute tw-inset-y-0 tw-z-20 tw-cursor-pointer tw-appearance-none tw-border-0 tw-bg-transparent tw-p-0 tw-outline-none focus-visible:tw-outline-focus'
      data-hero-music-control
      onClick={handleClick}
      onPointerCancel={stopSectionEvent}
      onPointerDown={handlePointerDown}
      onPointerEnter={() => onHoverSectionChange(sectionIndex)}
      onPointerLeave={() => onHoverSectionChange(null)}
      onPointerMove={stopSectionEvent}
      onPointerUp={stopSectionEvent}
      style={{
        left: `${timestampToPercent(section.start, timelineDuration)}%`,
        width: `${(sectionDuration / timelineDuration) * 100}%`,
      }}
      type='button'
    />
  );
}

function useActiveTimelineSectionIndex(timeline: HeroMusicTimeline, progress: MotionValue<number>) {
  const [activeSectionIndex, setActiveSectionIndex] = useState(() => getActiveSectionIndex(timeline, progress.get()));

  function syncActiveSectionIndex(progressValue: number) {
    const nextSectionIndex = getActiveSectionIndex(timeline, progressValue);

    setActiveSectionIndex(currentSectionIndex =>
      currentSectionIndex === nextSectionIndex ? currentSectionIndex : nextSectionIndex,
    );
  }

  useEffect(() => {
    syncActiveSectionIndex(progress.get());
  }, [progress, timeline]);

  useMotionValueEvent(progress, "change", latestProgress => {
    syncActiveSectionIndex(latestProgress);
  });

  return activeSectionIndex;
}

function useAnimatedSegmentEndpoints(segment: SegmentTarget) {
  const targetX1 = useMotionValue(segment.x1);
  const targetX2 = useMotionValue(segment.x2);
  const x1 = useSpring(targetX1, GAP_SPRING);
  const x2 = useSpring(targetX2, GAP_SPRING);

  useEffect(() => {
    targetX1.set(segment.x1);
    targetX2.set(segment.x2);
  }, [segment.x1, segment.x2, targetX1, targetX2]);

  const width = useTransform([x1, x2], ([latestX1, latestX2]) => Math.max(Number(latestX2) - Number(latestX1), 0));
  const visibleX2 = useTransform([x1, width], ([latestX1, latestWidth]) => Number(latestX1) + Number(latestWidth));
  const opacity = useTransform(width, latest => (latest <= 0.01 ? 0 : 1));

  return { opacity, width, x1, x2: visibleX2 };
}

function getSegmentTargets(timeline: HeroMusicTimeline, sectionSlotCount: number) {
  const gapSlotCount = getGapSlotCount(sectionSlotCount);
  const targets = getGapTargets(timeline);
  const fallbackX = targets.at(-1) ?? BAR_WIDTH / 2;
  const gapCenters = Array.from({ length: gapSlotCount }, (_, index) => targets[index] ?? fallbackX);

  return Array.from({ length: gapSlotCount + 1 }, (_, index) => {
    const x1 = index === 0 ? 0 : gapCenters[index - 1] + HALF_SEGMENT_GAP;
    const x2 = index === gapSlotCount ? BAR_WIDTH : gapCenters[index] - HALF_SEGMENT_GAP;
    const clampedX1 = clampValue(x1, 0, BAR_WIDTH);
    const clampedX2 = clampValue(x2, 0, BAR_WIDTH);

    return {
      id: `segment-${index}`,
      x1: clampedX1,
      x2: clampedX2,
    };
  });
}

function getSectionLabels(timeline: HeroMusicTimeline, sectionSlotCount: number) {
  const targets = timeline.sections.map((section, sectionIndex) => ({
    label: section.label,
    sectionIndex,
    width: timestampToPercent(section.end - section.start, timeline.duration),
    x: timestampToPercent(section.start, timeline.duration),
  }));
  const fallbackLabel = targets.at(-1);

  if (!fallbackLabel) return [];

  return Array.from({ length: getLabelSlotCount(sectionSlotCount) }, (_, index) => {
    const target = targets[index] ?? fallbackLabel;
    const isPlaceholder = !targets[index];

    return {
      isPlaceholder,
      slotId: `label-${index}`,
      ...target,
    } satisfies SectionLabel;
  });
}

function getLabelOpacity(label: SectionLabel, activeSectionIndex: number | null, hoveredSectionIndex: number | null) {
  if (label.isPlaceholder) return 0;
  if (label.sectionIndex === activeSectionIndex || label.sectionIndex === hoveredSectionIndex) return 1;

  return 0.6;
}

function getActiveSectionIndex(timeline: HeroMusicTimeline, progressValue: number) {
  const sections = timeline.sections;

  if (sections.length === 0 || timeline.duration <= 0) return null;

  const playbackTime = clampValue(progressValue, 0, 1) * timeline.duration;

  for (let sectionIndex = sections.length - 1; sectionIndex >= 0; sectionIndex -= 1) {
    if (playbackTime >= sections[sectionIndex].start) return sectionIndex;
  }

  return 0;
}

function getGapTargets(timeline: HeroMusicTimeline) {
  return timeline.sections.slice(0, -1).map(section => timestampToX(section.end, timeline.duration));
}

function getGapSlotCount(sectionSlotCount: number) {
  return Math.max(sectionSlotCount - 1, 0);
}

function getLabelSlotCount(sectionSlotCount: number) {
  return Math.max(sectionSlotCount, 0);
}

function timestampToX(timestamp: number, duration: number) {
  return timestampToProgress(timestamp, duration) * BAR_WIDTH;
}

function timestampToProgress(timestamp: number, duration: number) {
  return clampValue(timestamp, 0, duration) / duration;
}

function timestampToPercent(timestamp: number, duration: number) {
  return timestampToProgress(timestamp, duration) * 100;
}
